import { useTickets } from '@/hooks/useTickets';
import { useAuth } from '@/hooks/useAuth';
import { useUserWorkflow, useWorkflowsByDepartmentId } from '@/hooks/useWorkflows';
import { useLeadDepartment, useAgentDepartment } from '@/hooks/useConfig';
import { useTasks, useLeadDashboard } from '@/hooks/useTasks';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Ticket, CheckCircle2, AlertTriangle, TrendingUp, BarChart3, ListTodo, Calendar, Users } from 'lucide-react';
import { PriorityBadge } from '@/components/ui/priority-badge';
import { StageBadge } from '@/components/ui/stage-badge';
import { format, subDays, isAfter, isBefore, addDays } from 'date-fns';
import { Link } from 'react-router-dom';

export function DashboardPage() {
  const { user, isTeamLeader, isAgent } = useAuth();
  const { leadDepartmentId } = useLeadDepartment();
  const { agentDepartmentId } = useAgentDepartment();
  const departmentIdForWorkflow = isTeamLeader ? leadDepartmentId : isAgent ? agentDepartmentId : undefined;
  const ticketFilters = isTeamLeader && leadDepartmentId ? { departmentId: leadDepartmentId } : undefined;
  const { data: workflow, isLoading: isWorkflowLoading } = useUserWorkflow(user?.id);
  const { data: departmentWorkflows = [], isLoading: isDepartmentWorkflowsLoading } = useWorkflowsByDepartmentId(departmentIdForWorkflow ?? undefined);
  const { tickets, stages, isLoading: isTicketsLoading } = useTickets(ticketFilters);
  const { tasks, isLoading: isTasksLoading } = useTasks();
  const { data: leadDashboard, isLoading: isLeadDashboardLoading } = useLeadDashboard(isTeamLeader);

  const isDeptUser = isTeamLeader || isAgent;
  const isLoading =
    isTicketsLoading ||
    isWorkflowLoading ||
    (isTeamLeader && (isLeadDashboardLoading || isTasksLoading || isDepartmentWorkflowsLoading)) ||
    (isAgent && (isTasksLoading || isDepartmentWorkflowsLoading));
  const now = new Date();
  const in7Days = addDays(now, 7);
  const overdueTasksAgent = tasks.filter((t) => t.dueDate && isBefore(new Date(t.dueDate), in7Days));
  const tasksByStage = (() => {
    const byStage: Record<string, number> = {};
    tasks.forEach((t) => { byStage[t.stageId] = (byStage[t.stageId] ?? 0) + 1; });
    return byStage;
  })();

  const totalDeptTasksFromLead =
    leadDashboard?.departmentTasksByStage.reduce((sum, s) => sum + s.count, 0) ?? 0;
  const leadWorkflowStageCount = leadDashboard?.departmentTasksByStage.length ?? 0;
  const overdueTasksFromLead = leadDashboard?.overdueTasks ?? [];

  // Calculate stats
  const today = new Date();
  const last7Days = subDays(today, 7);

  const ticketsThisWeek = tickets.filter(t =>
    t.created_at && isAfter(new Date(t.created_at), last7Days)
  ).length;

  const openTickets = tickets.filter(t => {
    const stage = stages.find(s => s.id === t.stage_id);
    return stage && !['Closed', 'Resolved'].includes(stage.name);
  }).length;

  const closedTickets = tickets.filter(t => {
    const stage = stages.find(s => s.id === t.stage_id);
    return stage && stage.name === 'Closed';
  }).length;

  const criticalTickets = tickets.filter(t => t.priority === 'critical');
  const highPriorityTickets = tickets.filter(t => t.priority === 'high' || t.priority === 'critical');

  // Determine which stages to display for "Tasks by Stage".
  // Team leader / Agent: use workflow assigned to their department only.
  // Others: use user workflow or all stages.
  let displayStages = stages;
  if (isDeptUser && departmentWorkflows.length > 0 && departmentWorkflows[0]?.workflowStages?.length) {
    const deptWorkflow = departmentWorkflows[0];
    displayStages = [...(deptWorkflow.workflowStages ?? [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map(ws => ws.stage);
  } else if (!isDeptUser && workflow?.workflowStages?.length) {
    displayStages = [...workflow.workflowStages]
      .sort((a, b) => a.position - b.position)
      .map(ws => ws.stage);
  }

  // Tasks by stage: team leaders use GET /api/tasks/lead-dashboard (TASKS_FLOW.md)
  const tasksByStageList = isTeamLeader && leadDashboard
    ? leadDashboard.departmentTasksByStage.map((row) => ({
        stage: {
          id: row.stageId,
          name: row.stageName,
          color: row.stageColor,
        },
        count: row.count,
      }))
    : displayStages.map((stage) => ({
        stage,
        count: tasks.filter((t) => t.stageId === stage.id).length,
      }));

  // Recent: team leader / agent see recent tasks, others see recent tickets
  const recentTickets = tickets.slice(0, 5);
  const recentTasks = [...tasks].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);

  const stats = [
    {
      title: 'Open Tickets',
      value: openTickets,
      icon: Ticket,
      color: 'text-info',
      bgColor: 'bg-info/10',
    },
    {
      title: 'Closed This Month',
      value: closedTickets,
      icon: CheckCircle2,
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      title: 'High Priority',
      value: highPriorityTickets.length,
      icon: AlertTriangle,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
    {
      title: 'New This Week',
      value: ticketsThisWeek,
      icon: TrendingUp,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">
          {isDeptUser ? 'Department overview and task progress' : 'Overview of your ticketing system'}
        </p>
      </div>

      {/* Task summary (team leader / agent) */}
      {isDeptUser && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Tasks</p>
                    <p className="text-3xl font-bold mt-1">
                      {isTeamLeader ? totalDeptTasksFromLead : tasks.length}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-primary/10">
                    <ListTodo className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Workflow stages</p>
                    <p className="text-2xl font-bold mt-1">
                      {isTeamLeader ? leadWorkflowStageCount : Object.keys(tasksByStage).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {isTeamLeader ? 'Overdue tasks' : 'Due within 7 days'}
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {isTeamLeader ? overdueTasksFromLead.length : overdueTasksAgent.length}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-destructive/10">
                    <Calendar className="h-6 w-6 text-destructive" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {isTeamLeader && overdueTasksFromLead.length > 0 && (
            <Card className="border-amber-500/50 bg-amber-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">Overdue tasks</CardTitle>
                <CardDescription>Tasks past due date in default stages (department)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overdueTasksFromLead.slice(0, 5).map((task) => (
                    <Link
                      key={task.id}
                      to={`/admin/tasks/${task.id}`}
                      className="flex items-center justify-between gap-2 p-3 rounded-lg bg-card border hover:bg-muted/50"
                    >
                      <span className="font-medium min-w-0 truncate">{task.title}</span>
                      <span className="text-sm text-muted-foreground shrink-0">
                        {task.dueDate ? format(new Date(task.dueDate), 'MMM d') : '—'} · {task.priority} ·{' '}
                        {task.overdueDays}d overdue
                      </span>
                    </Link>
                  ))}
                  {overdueTasksFromLead.length > 5 && (
                    <Link to="/admin/kanban?view=list" className="text-sm text-primary hover:underline">
                      View all tasks →
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {isAgent && overdueTasksAgent.length > 0 && (
            <Card className="border-amber-500/50 bg-amber-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">Tasks due soon / overdue</CardTitle>
                <CardDescription>Deadline within the next 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overdueTasksAgent.slice(0, 5).map((task) => (
                    <Link
                      key={task.id}
                      to={`/admin/tasks/${task.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-card border hover:bg-muted/50"
                    >
                      <span className="font-medium">{task.title}</span>
                      <span className="text-sm text-muted-foreground">
                        {task.dueDate ? format(new Date(task.dueDate), 'MMM d') : '—'} · {task.priority}
                      </span>
                    </Link>
                  ))}
                  {overdueTasksAgent.length > 5 && (
                    <Link to="/admin/kanban?view=list" className="text-sm text-primary hover:underline">
                      View all tasks →
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {isTeamLeader && leadDashboard && leadDashboard.agents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Agent performance
                </CardTitle>
                <CardDescription>On-time completed tasks vs assigned (from lead dashboard)</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Agent</th>
                      <th className="py-2 pr-4 font-medium">Assigned</th>
                      <th className="py-2 pr-4 font-medium">On-time done</th>
                      <th className="py-2 font-medium">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadDashboard.agents.map((agent) => (
                      <tr key={agent.id} className="border-b border-border/60 last:border-0">
                        <td className="py-3 pr-4">
                          <span className="font-medium">{agent.name ?? agent.email}</span>
                        </td>
                        <td className="py-3 pr-4">{agent.totalAssignedTasks}</td>
                        <td className="py-3 pr-4">{agent.completedOnTime}</td>
                        <td className="py-3">
                          {agent.performanceScore === null ? '—' : `${agent.performanceScore}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks by Stage */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Tasks by Stage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {tasksByStageList.map(({ stage, count }) => (
              <div key={stage.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: stage.color }}
                  />
                  <span className="text-sm">{stage.name}</span>
                </div>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Tasks (team leader / agent) / Recent Tickets (others) */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{isDeptUser ? 'Recent Tasks' : 'Recent Tickets'}</CardTitle>
              <CardDescription>
                {isDeptUser ? 'Latest tasks in your department' : 'Latest tickets submitted'}
              </CardDescription>
            </div>
            <Link to={isDeptUser ? (isTeamLeader ? '/admin/kanban?view=list' : '/admin/tasks') : '/admin/tickets'}>
              <span className="text-sm text-primary hover:underline">View all</span>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {isDeptUser ? (
                recentTasks.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No tasks yet</p>
                ) : (
                  recentTasks.map((task) => (
                    <Link
                      key={task.id}
                      to={`/admin/tasks/${task.id}`}
                      className="block p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{task.title}</span>
                            {task.stage && (
                              <span
                                className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium border"
                                style={{ borderColor: task.stage.color ?? '#ccc', backgroundColor: `${task.stage.color ?? '#eee'}20` }}
                              >
                                {task.stage.name}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {task.description}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="capitalize">{task.priority}</span>
                            <span>•</span>
                            <span>
                              {task.createdAt
                                ? format(new Date(task.createdAt), 'MMM d, h:mm a')
                                : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs font-medium capitalize shrink-0">{task.priority}</span>
                      </div>
                    </Link>
                  ))
                )
              ) : recentTickets.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No tickets yet</p>
              ) : (
                recentTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/admin/tickets/${ticket.id}`}
                    className="block p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{(ticket as { ticketNumber?: string; ticket_number?: string }).ticketNumber ?? (ticket as { ticket_number?: string }).ticket_number}</span>
                          {ticket.stage && <StageBadge stage={ticket.stage} />}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {ticket.description}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>{(ticket as { requesterName?: string; requester_name?: string }).requesterName ?? (ticket as { requester_name?: string }).requester_name}</span>
                          <span>•</span>
                          <span>
                            {(() => {
                              const d = (ticket as { createdAt?: string; created_at?: string }).createdAt ?? (ticket as { created_at?: string }).created_at;
                              return d ? format(new Date(d), 'MMM d, h:mm a') : 'N/A';
                            })()}
                          </span>
                        </div>
                      </div>
                      <PriorityBadge priority={ticket.priority} showIcon={false} />
                    </div>
                  </Link>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Critical Tickets Alert */}
      {criticalTickets.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Critical Tickets Requiring Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {criticalTickets.slice(0, 3).map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/admin/tickets/${ticket.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-card border hover:bg-muted/50 transition-colors"
                >
                  <div>
                    <span className="font-medium">{ticket.ticket_number}</span>
                    <span className="text-muted-foreground mx-2">—</span>
                    <span className="text-sm">{ticket.requester_name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {ticket.created_at 
                      ? format(new Date(ticket.created_at), 'MMM d')
                      : 'N/A'}
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}